import { ContractPersonsSideloading } from '@ekt-group/general-purpose-api-interfaces/src/modules/contracts/types/contract-persons.enum';
import { Plus } from '@untitled-ui/icons-react';
import { Empty, Modal, Table } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/components/ui/Button';
import { useGetContractPersons } from '@/features/contracts/api/use-get-contract-persons';
import { useAttorneysTable } from '@/features/contracts/hooks/use-attorneys-table';
import { useBreakpoint } from '@/hooks/use-breakpoints';
import { useRowExpand } from '@/hooks/use-row-expand';
import { CONTRACT_MODE, useContractModalStore } from '@/store/use-contract-modal-store';
import { cn } from '@/utils/cn';

interface AttorneysModalProps {
  contractId: number;
  contractNumber: string;
  isModalOpen: boolean;
  handleClose: () => void;
  setContractPersonId: (personId: number) => void;
}

const AttorneysModal = ({ contractId, contractNumber, isModalOpen, handleClose, setContractPersonId }: AttorneysModalProps) => {
  const { t } = useTranslation('contractAttorneyModal');

  const { setMode } = useContractModalStore();

  const contractPersonsQuery = useGetContractPersons({
    params: {
      contractIds: [contractId],
      sideloading: [ContractPersonsSideloading.ContractOwner],
      disablePagination: true,
    },
  });

  const handleEditButtonClick = (contractPersonId: number) => {
    setContractPersonId(contractPersonId);
    setMode(CONTRACT_MODE.EDIT_ATTORNEY);
  };

  const handleDeleteButtonClick = (contractPersonId: number) => {
    setContractPersonId(contractPersonId);
    setMode(CONTRACT_MODE.DELETE_ATTORNEY);
  };

  const handleAddButtonClick = () => {
    setMode(CONTRACT_MODE.ADD_ATTORNEY);
  };

  const { isAboveSm } = useBreakpoint('sm');

  const { handleExpand, expandedRowKeys } = useRowExpand();
  const { columns, expandedRowRender } = useAttorneysTable({
    expandedRowKeys,
    handleExpand,
    handleEditButtonClick,
    handleDeleteButtonClick,
  });

  return (
    <Modal
      centered
      onCancel={handleClose}
      width="90%"
      styles={{
        body: {
          height: '60vh',
          overflowY: 'auto',
        },
        content: {
          justifySelf: 'center',
          borderRadius: 24,
          width: '100%',
        },
      }}
      closable
      open={isModalOpen}
      onClose={handleClose}
      title={
        <div className="flex sm:flex-row flex-col sm:gap-4 sm:items-center pb-3 border-b border-b-[#EFEFEF]">
          <span className="text-[20px] sm:text-[24px] font-bold text-gray-10">{t('manageAttorneys')}</span>
          <span className="text-[20px] mb-4 sm:mb-0 sm:text-[24px] font-bold text-[#A7AD2A]">L{contractNumber}</span>
          <Button onClick={handleAddButtonClick} variant="primary" className="h-9 px-3">
            <Plus className="text-gray-10 w-4 h-4" />
            <span className="text-base text-gray-10">{t('add', { ns: 'contractModal' })}</span>
          </Button>
        </div>
      }
      footer={
        <div className="flex justify-end">
          <Button onClick={handleClose} className="h-12 sm:w-[248px] w-full sm:justify-self-end justify-center" variant="secondaryAlt">
            {t('close', { ns: 'table' })}
          </Button>
        </div>
      }
    >
      <Table
        onRow={(record) => ({
          onClick: (event) => handleExpand(record.id.toString(), event, !isAboveSm),
        })}
        rowClassName={(record) =>
          cn('!h-[65px]', {
            'bg-[#F9FAE0]': expandedRowKeys.includes(record.id.toString()),
          })
        }
        rowKey={(record) => record.id.toString()}
        className="w-full rounded-[32px]"
        loading={contractPersonsQuery.isLoading}
        locale={{
          emptyText: <Empty description={t('noData', { ns: 'table' })} />,
        }}
        expandable={{
          expandedRowClassName: () => 'bg-[#F9FAE0] !mx-0 !px-0 [&_.ant-table-cell]:!p-0',
          expandedRowKeys,
          expandedRowRender,
          rowExpandable: () => true,
          showExpandColumn: false,
        }}
        pagination={false}
        dataSource={contractPersonsQuery.data?.data || []}
        scroll={{ x: 'max-content' }}
        columns={columns}
      />
    </Modal>
  );
};

export default AttorneysModal;
